import React, { useState, useEffect, useCallback } from "react";
import { saisirSuiviOperations, getSuiviJour, deleteSuiviSaisie } from "../services/api";
import ModaleV2 from "../components/ModaleV2";
import SuiviTableauDeBordV2, { TYPES_OPERATIONS } from "../components/SuiviTableauDeBordV2";

// ===================================================================
// The Crowd v2 — Gestion utilisateurs › Suivi
//
// Saisie legere du nombre d'operations sur les comptes : creations,
// modifications, desactivations. Le technicien est celui de la session.
// Date du jour par defaut, rattrapage possible sur un mois. Les saisies
// d'une journee s'additionnent (plusieurs techniciens, plusieurs fois).
// Chacun voit le cumul du jour et peut supprimer ses propres saisies.
// Managers et directeurs voient en plus le tableau de bord.
// Backend : suivi_operations.py.
// ===================================================================

const ROLES_TABLEAU_DE_BORD = ["manager", "directeur"];
const JOURS_RATTRAPAGE = 31;
const VALEURS_VIDES = { creations: "", modifications: "", desactivations: "" };

// Date locale au format AAAA-MM-JJ (et non UTC : la saisie suit le jour du technicien)
function dateLocale(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function decaler(jours) {
  const d = new Date();
  d.setDate(d.getDate() + jours);
  return dateLocale(d);
}

function heure(iso) {
  if (!iso) return "";
  const d = new Date(String(iso).endsWith("Z") ? iso : `${iso}Z`);
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function libelleSaisie(s) {
  return TYPES_OPERATIONS.filter((t) => s[t.cle] > 0)
    .map((t) => `${s[t.cle]} ${t.libelle.toLowerCase()}`)
    .join(", ");
}

function SuiviOperationsV2({ collaborateur }) {
  const aujourdhui = dateLocale();
  const [dateJour, setDateJour] = useState(aujourdhui);
  const [valeurs, setValeurs] = useState(VALEURS_VIDES);
  const [jour, setJour] = useState(null);
  const [erreurJour, setErreurJour] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState(null);
  const [aSupprimer, setASupprimer] = useState(null);
  const [rafraichir, setRafraichir] = useState(0);

  const chargerJour = useCallback(() => {
    if (!collaborateur || !dateJour) return Promise.resolve();
    setErreurJour(null);
    return getSuiviJour(dateJour, collaborateur.id)
      .then(setJour)
      .catch((err) => setErreurJour(`Cumul du jour non chargé : ${err.message}`));
  }, [collaborateur, dateJour]);

  useEffect(() => {
    chargerJour();
  }, [chargerJour]);

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">Compte non configuré. Contactez votre manager.</div>
      </div>
    );
  }

  const nombre = (cle) => {
    const n = parseInt(valeurs[cle], 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const totalSaisi = TYPES_OPERATIONS.reduce((s, t) => s + nombre(t.cle), 0);
  const dateValide = dateJour && dateJour <= aujourdhui && dateJour >= decaler(-JOURS_RATTRAPAGE);

  const changer = (cle) => (e) => {
    const v = e.target.value;
    if (v === "" || /^\d{1,3}$/.test(v)) setValeurs((x) => ({ ...x, [cle]: v }));
  };

  const enregistrer = async () => {
    setEnCours(true);
    setMessage(null);
    try {
      const data = { date_jour: dateJour };
      TYPES_OPERATIONS.forEach((t) => { data[t.cle] = nombre(t.cle); });
      await saisirSuiviOperations(data, collaborateur.id);
      setMessage({ type: "succes", texte: `Enregistré : ${libelleSaisie(data)}.` });
      setValeurs(VALEURS_VIDES);
      await chargerJour();
      setRafraichir((n) => n + 1);
    } catch (err) {
      setMessage({ type: "erreur", texte: `Enregistrement impossible : ${err.message}` });
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async () => {
    try {
      await deleteSuiviSaisie(aSupprimer.id, collaborateur.id);
      setASupprimer(null);
      setMessage({ type: "succes", texte: "Saisie supprimée." });
      await chargerJour();
      setRafraichir((n) => n + 1);
    } catch (err) {
      setASupprimer(null);
      setMessage({ type: "erreur", texte: `Suppression impossible : ${err.message}` });
    }
  };

  const total = jour?.total;
  const libelleJour = dateJour === aujourdhui ? "aujourd'hui" : `le ${new Date(`${dateJour}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}`;

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Suivi</h1>
        <p className="v2-sous-titre">Créations, modifications et désactivations de comptes traitées dans la journée.</p>
      </header>

      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}

      <div className="v2-suivi-haut">
        {/* Saisie */}
        <section className="v2-panneau v2-suivi-saisie" aria-labelledby="v2-suivi-saisie-titre">
          <h2 id="v2-suivi-saisie-titre" className="v2-bloc-titre">Ma saisie</h2>
          <div className="v2-suivi-formulaire">
            <div className="v2-champ">
              <label className="v2-label" htmlFor="v2-suivi-date">Date</label>
              <input id="v2-suivi-date" type="date" value={dateJour} max={aujourdhui} min={decaler(-JOURS_RATTRAPAGE)}
                onChange={(e) => setDateJour(e.target.value)} />
            </div>
            {TYPES_OPERATIONS.map((t) => (
              <div key={t.cle} className="v2-champ">
                <label className="v2-label" htmlFor={`v2-suivi-${t.cle}`}>{t.libelle}</label>
                <input id={`v2-suivi-${t.cle}`} type="number" inputMode="numeric" min="0" max="999" step="1"
                  placeholder="0" value={valeurs[t.cle]} onChange={changer(t.cle)} className="v2-suivi-nombre" />
              </div>
            ))}
            <button type="button" className="v2-btn v2-btn-principal v2-suivi-bouton" onClick={enregistrer}
              disabled={enCours || totalSaisi === 0 || !dateValide}>
              {enCours ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
          {!dateValide && dateJour && (
            <span className="v2-champ-aide v2-texte-danger">Date hors période : aujourd'hui ou jusqu'à {JOURS_RATTRAPAGE} jours en arrière.</span>
          )}
          <span className="v2-champ-aide">Plusieurs saisies dans la journée s'additionnent.</span>
        </section>

        {/* Cumul du jour et mes saisies */}
        <section className="v2-panneau v2-suivi-jour" aria-labelledby="v2-suivi-jour-titre">
          <h2 id="v2-suivi-jour-titre" className="v2-bloc-titre">Cumul {libelleJour}</h2>
          {erreurJour && <div className="v2-msg v2-msg-erreur">{erreurJour}</div>}
          {total && (
            <>
              <div className="v2-suivi-cumul">
                {TYPES_OPERATIONS.map((t) => (
                  <div key={t.cle} className="v2-suivi-cumul-item">
                    <span className="v2-suivi-cumul-valeur">{total[t.cle]}</span>
                    <span className="v2-suivi-cumul-libelle">
                      <span className={`v2-suivi-pastille ${t.classe}`} aria-hidden="true" />{t.libelle}
                    </span>
                  </div>
                ))}
              </div>
              <span className="v2-cellule-discrete">
                {total.nb_techniciens === 0
                  ? "Aucune saisie ce jour-là."
                  : `${total.nb_techniciens} technicien${total.nb_techniciens > 1 ? "s ont" : " a"} saisi.`}
              </span>
            </>
          )}

          {jour?.mes_saisies?.length > 0 && (
            <div className="v2-suivi-mes-saisies">
              <span className="v2-label">Mes saisies</span>
              <ul>
                {jour.mes_saisies.map((s) => (
                  <li key={s.id}>
                    <span className="v2-cellule-discrete">{heure(s.created_at)}</span>
                    <span>{libelleSaisie(s)}</span>
                    <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact v2-btn-danger-texte" onClick={() => setASupprimer(s)}>
                      Supprimer
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>

      {ROLES_TABLEAU_DE_BORD.includes(collaborateur.role) && (
        <SuiviTableauDeBordV2 collaborateurId={collaborateur.id} rafraichir={rafraichir} />
      )}

      {aSupprimer && (
        <ModaleV2
          titre="Supprimer la saisie"
          onClose={() => setASupprimer(null)}
          actions={
            <>
              <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setASupprimer(null)}>Annuler</button>
              <button type="button" className="v2-btn v2-btn-principal" onClick={supprimer}>Supprimer</button>
            </>
          }
        >
          <p className="v2-champ-aide">
            La saisie de {heure(aSupprimer.created_at)} ({libelleSaisie(aSupprimer)}) sera retirée du cumul.
          </p>
        </ModaleV2>
      )}
    </div>
  );
}

export default SuiviOperationsV2;
